import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import { createContext, ReactNode, useContext } from "react";
import { useSelector } from "react-redux";

/** Whether the one lookup of every person a comparison names is still in flight. */
const ComparedPeopleLoading = createContext(false);

/**
 * Looks up every person a comparison names in one request, so a cell only reads the store. A cell
 * that sent its own request asked once per person: four firms with fifteen categories each could
 * send sixty.
 */
export function ComparedPeople(
  props: Readonly<{ userIds: readonly Guid[]; children: ReactNode }>,
) {
  const { userIds, children } = props;
  // Sorted, so the same people in another order are the same request.
  const values = userIds.map((userId) => userId.value).sort();
  const query = useGetIndividualsByUserIdsQuery(
    values.length > 0 ? { userIds: values } : skipToken,
  );
  return (
    <ComparedPeopleLoading.Provider value={query.isLoading}>
      {children}
    </ComparedPeopleLoading.Provider>
  );
}

/** A person a comparison names, such as who bills a fee category, by their profile's name. */
export default function ComparedPersonName(props: Readonly<{ userId: Guid }>) {
  const { userId } = props;
  const individual = useSelector(getIndividualByUserId(userId));
  const isLoading = useContext(ComparedPeopleLoading);
  if (individual?.fullName) return <>{individual.fullName}</>;
  if (isLoading) return <>Loading name</>;
  return <>A team member</>;
}
